import {
  Align,
  Arrange,
  Button,
  Container,
  Icon,
  Loader,
  Spacer,
  SvgClock,
  Text,
} from '@loomhq/lens';
import produce from 'immer';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { showConfirmationModal } from 'actions/confirmationModal';
import { updateShareVideoInTrimMode } from 'actions/shareVideo';
import {
  updateSelectedPlayRange,
  updateShowTrimExitWarning,
} from 'actions/trimming';
import {
  requestVideoPlayerCurrentTime,
  setCurrentCaption,
  updateVideoPlayerCurrentTime,
  updateVideoPlayerEnded,
  updateVideoPlayerNotEnded,
} from 'actions/videoPlayer';
import { INFO, TOAST_MESSAGE_DURATION } from 'constants/error-severities';
import {
  TRIM_CANCELLED,
  TRIM_CHANGE_MADE,
  TRIM_DELETE,
  TRIM_REVERT_TO_ORIGINAL,
} from 'constants/events';
import {
  deletePlayRangeCreator,
  leaveTrimModeCreator,
  patchTrimVideoCreator,
  redoPlayRangeChangeCreator,
  splitPlayRangeCreator,
  syncPlayRangesFromTrimRangesCreator,
  undoPlayRangeChangeCreator,
  updatePlayRangesCreator,
} from 'creators/trimming';
import { updateVideoPlayerDurationCreator } from 'creators/videoPlayer';
import * as analytics from 'utilities/analytics';
import { secondsToVideoTS } from 'utilities/time';
import {
  getTrimVideoDuration,
  makePlayRangeId,
  makeTrimRangesFromPlayRanges,
} from 'utilities/trimming';

import {
  selectCanDeletePlayRange,
  selectCanRedoPlayRange,
  selectCanSplitPlayRange,
  selectCanUndoPlayRange,
  selectPlayRanges,
  selectSelectedPlayRange,
  selectTrimNewChanges,
} from 'selectors/trimming';
import {
  selectVideoHasAudioEnabled,
  selectVideoId,
  selectVideoWaveformStatus,
  selectVideoWaveformUrl,
} from 'selectors/video';
import {
  selectPlayerTime,
  selectVideo,
  selectVideoDuration,
  selectVideoPlayerIsFetching,
  selectVideoPlayerReadyToPlayOnSharePage,
} from 'selectors/videoPlayer';

import ControlBar from './control-bar';
import Waveform from './waveform';

import styles from './styles.module.less';

const TrimTools = (): JSX.Element => {
  const [waveformData, setWaveformData] = useState([]);

  const dispatch = useDispatch();
  const currentTime = useSelector(selectPlayerTime);
  const duration = useSelector(selectVideoDuration);
  const waveformUrl = useSelector(selectVideoWaveformUrl);
  const waveformGenerationStatus = useSelector(selectVideoWaveformStatus);
  const selectedPlayRange = useSelector(selectSelectedPlayRange);
  const newTrimChanges = useSelector(selectTrimNewChanges);
  const canUndo = useSelector(selectCanUndoPlayRange);
  const canRedo = useSelector(selectCanRedoPlayRange);
  const video = useSelector(selectVideo);
  const videoHasAudio = useSelector(selectVideoHasAudioEnabled);
  const canSplitPlayRange = useSelector(selectCanSplitPlayRange);
  const canDeletePlayRange = useSelector(selectCanDeletePlayRange);

  const playRanges = useSelector(selectPlayRanges);
  const videoId = useSelector(selectVideoId);
  const trimRanges = makeTrimRangesFromPlayRanges(playRanges, duration);

  const isFetching = useSelector(selectVideoPlayerIsFetching);
  const readyToPlay = useSelector(selectVideoPlayerReadyToPlayOnSharePage);

  const trimButtonsRef = useCallback(buttonsNode => {
    if (buttonsNode) {
      buttonsNode.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }
  }, []);

  // This effect is here b/c we do not have an accurate duration when the
  // video first loads. The duration in video_player is the duration of the
  // trimmed video and the duration in video_player.video.video_properties
  // is rounded which can mess up our play range values.
  useEffect(() => {
    dispatch(syncPlayRangesFromTrimRangesCreator());
  }, [dispatch, duration]);

  useEffect(() => {
    const getWaveformJSON = async () => {
      if (!waveformUrl) {
        return;
      }

      const resp = await fetch(waveformUrl);

      if (!resp.ok) {
        throw new Error('Failed to fetch waveform json');
      }

      const data = await resp.json();

      setWaveformData(data);
    };

    if (waveformUrl) {
      getWaveformJSON();
    }
  }, [waveformUrl]);

  // Handle URL changes, back button, page reload with unsaved changes
  const unloadEventListenerRef = useRef(null);

  useEffect(() => {
    const showWarningModal = event => {
      event.preventDefault();

      return (event.returnValue =
        'There are unsaved changes to your video. Are you sure you want to discard them?');
    };

    if (unloadEventListenerRef.current) {
      window.removeEventListener(
        'beforeunload',
        unloadEventListenerRef.current,
        { capture: true }
      );
    }

    if (newTrimChanges) {
      unloadEventListenerRef.current = showWarningModal;
      window.addEventListener('beforeunload', unloadEventListenerRef.current, {
        capture: true,
      });
    }

    return () => {
      if (unloadEventListenerRef.current) {
        window.removeEventListener(
          'beforeunload',
          unloadEventListenerRef.current,
          { capture: true }
        );
      }
    };
  }, [newTrimChanges, dispatch]);

  /* keep the playhead inside the play ranges */
  const placePlayheadInPlayRanges = () => {
    const rangeIdx = playRanges.findIndex(
      range => range.from <= currentTime && range.to >= currentTime
    );

    // If we are in a play range already
    if (rangeIdx !== -1) {
      dispatch(updateSelectedPlayRange(rangeIdx));

      return;
    }

    // If we are not in a play range, move to the closest one
    let newPlayRange: number;
    let closestLoc: number;
    let distance = Infinity;

    for (let idx = 0; idx < playRanges.length; idx++) {
      const range = playRanges[idx];

      if (Math.abs(range.from - currentTime) < distance) {
        closestLoc = range.from;
        newPlayRange = idx;
        distance = Math.abs(range.from - currentTime);
      }

      if (Math.abs(range.to - currentTime) < distance) {
        closestLoc = range.to;
        newPlayRange = idx;
        distance = Math.abs(range.to - currentTime);
      }
    }

    dispatch(updateVideoPlayerCurrentTime(closestLoc));
    dispatch(updateSelectedPlayRange(newPlayRange));
  };

  useEffect(placePlayheadInPlayRanges, [
    dispatch,
    playRanges,
    currentTime,
    selectedPlayRange,
  ]);

  useEffect(() => {
    // If we are at the end of the last play range
    if (currentTime === playRanges[playRanges.length - 1]?.to) {
      dispatch(updateVideoPlayerEnded());
    } else {
      dispatch(updateVideoPlayerNotEnded());
    }
  }, [currentTime, playRanges, dispatch]);

  const updatePlayRange = ({ from, to, id }) => {
    const newPlayRanges = produce(playRanges, draftRanges => {
      const range = draftRanges.find(range => range.id === id);

      if (range) {
        range.from = from;
        range.to = to;
      }
    });

    dispatch(updatePlayRangesCreator(newPlayRanges));
  };

  const deletePlayRange = () => {
    dispatch(deletePlayRangeCreator());

    analytics.track(TRIM_DELETE, {
      id: videoId,
      has_been_trimmed: true,
      event_type: 'click',
    });
  };

  const splitPlayRange = () => {
    dispatch(splitPlayRangeCreator());

    analytics.track(TRIM_CHANGE_MADE, {
      id: videoId,
      type: 'split',
      hotkey: false,
      has_been_trimmed: trimRanges.length > 0,
      event_type: 'click',
    });
  };

  const handleClick = (event: React.MouseEvent, { secs }: { secs: number }) => {
    dispatch(requestVideoPlayerCurrentTime(secs));
  };

  const saveChanges = () => {
    dispatch(
      patchTrimVideoCreator(video, () => {
        dispatch(updateShareVideoInTrimMode(false));
        dispatch(updateVideoPlayerDurationCreator(trimVideoDuration));
        dispatch(
          showConfirmationModal(
            'Your changes have been saved.',
            INFO,
            TOAST_MESSAGE_DURATION
          )
        );
      })
    );
    dispatch(setCurrentCaption(null));
  };

  const exitWithoutSaving = () => {
    if (newTrimChanges) {
      dispatch(updateShowTrimExitWarning(true));
    } else {
      goBackToVideo();
    }
  };

  const goBackToVideo = () => {
    dispatch(leaveTrimModeCreator());

    analytics.track(TRIM_CANCELLED, {
      id: videoId,
      has_been_trimmed: trimRanges.length > 0,
      event_type: 'click',
    });
  };

  const undo = () => {
    dispatch(undoPlayRangeChangeCreator());

    analytics.track(TRIM_CHANGE_MADE, {
      id: videoId,
      type: 'undo',
      hotkey: false,
      has_been_trimmed: trimRanges.length > 0,
      event_type: 'click',
    });
  };
  const redo = () => {
    dispatch(redoPlayRangeChangeCreator());

    analytics.track(TRIM_CHANGE_MADE, {
      id: videoId,
      type: 'redo',
      hotkey: false,
      has_been_trimmed: trimRanges.length > 0,
      event_type: 'click',
    });
  };

  const revertToOriginal = () => {
    dispatch(
      updatePlayRangesCreator([
        { id: makePlayRangeId(), from: 0, to: duration },
      ])
    );

    analytics.track(TRIM_REVERT_TO_ORIGINAL, {
      id: videoId,
      has_been_trimmed: trimRanges.length > 0,
      event_type: 'click',
    });
  };

  const canRevertToOriginal =
    playRanges.length > 0 &&
    (playRanges[0]?.from != 0 || playRanges[0]?.to != duration);

  const controlBarProps = {
    currentTime,
    canSplitPlayRange,
    splitPlayRange,
    canDeletePlayRange,
    deletePlayRange,
    canUndo,
    canRedo,
    undo,
    redo,
    revertToOriginal,
    canRevertToOriginal,
  };

  const isReady =
    !isFetching && playRanges && playRanges.length > 0 && readyToPlay;

  if (!isReady) {
    return (
      <Container paddingTop="xlarge">
        <Align alignment="center">
          <Loader size="large" />
        </Align>
      </Container>
    );
  }

  const trimVideoDuration = getTrimVideoDuration(duration, trimRanges);
  const durationChanged = duration !== trimVideoDuration;

  return (
    <Arrange gap="medium" justifyContent="stretch" autoFlow="row">
      <Spacer top="medium" />
      <ControlBar {...controlBarProps} />

      <Waveform
        viewboxHeight={80}
        peaks={waveformData}
        peakCount={1000}
        cursorWidth={2}
        cursorVerticalMargin={16}
        cursorPos={currentTime}
        duration={duration}
        normalize={true}
        sectionOutlineWidth={2}
        onClick={handleClick}
        playRanges={playRanges}
        selectedPlayRange={selectedPlayRange}
        updatePlayRange={updatePlayRange}
        waveformStatus={waveformGenerationStatus}
        hasAudio={videoHasAudio}
      />
      <div ref={trimButtonsRef}>
        <Align alignment="center">
          <div className={styles.durationChange}>
            {durationChanged ? (
              <Arrange gap="xsmall">
                <Icon color="bodyDimmed" icon={<SvgClock />} />{' '}
                <Text
                  size="medium"
                  color="bodyDimmed"
                  fontWeight="medium"
                  className={styles.timeDisplay}
                >
                  {secondsToVideoTS(duration, 1)}
                </Text>{' '}
                <Text size="medium" color="bodyDimmed" fontWeight="medium">
                  &rarr;
                </Text>
                <Text
                  size="medium"
                  color="body"
                  fontWeight="medium"
                  className={styles.timeDisplay}
                >
                  {secondsToVideoTS(trimVideoDuration, 1)}
                </Text>
              </Arrange>
            ) : (
              <Text size="medium">
                Trim your video by dragging the handles and using the Split
                button.
              </Text>
            )}
          </div>
          <Spacer top="medium" />
          <Arrange gap="small">
            <Button onClick={exitWithoutSaving}>Cancel</Button>
            <Button
              variant="primary"
              isDisabled={!newTrimChanges}
              onClick={saveChanges}
            >
              Save changes
            </Button>
          </Arrange>
        </Align>
      </div>
    </Arrange>
  );
};

export default TrimTools;
